import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, ChevronRight, ChevronDown, Download, FileText, FileDown, Filter, Store, ClipboardList } from 'lucide-react';
import * as XLSX from 'xlsx';
import useEhCompacto from '../../../utils/useEhCompacto';

// =====================================================================
// Consulta das avaliações BSV gravadas (tabela dinâmica + detalhe).
//
// Reorganizada em 2026-07-29 no padrão do Programa de Certificação
// (manutencao/certificacao/CertificacaoHistorico.js): é a TELA INICIAL do
// sub-hub — carrega a lista inteira uma vez, filtra em memória e apresenta
// os resultados numa árvore Loja → Versão → Avaliações.
//
// Por que carregar tudo e filtrar no cliente (e não no servidor, como antes):
// a lista de avaliações é pequena e o filtro fica instantâneo — sem esperar
// requisição a cada mudança de loja/período. O backend não mudou; a rota
// /historico só ganha os filtros quando eles são informados, e aqui não são.
// =====================================================================

const API_BASE = '/api/operacional/bsv';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Ordem canônica das faixas de conformidade — da melhor para a pior.
// Alimenta o select de filtro e os chips de contagem da linha da loja.
const CLASSIF_OPCOES = ['Excelente', 'Satisfatorio', 'Atencao', 'Critico', 'NA'];

// Cores das faixas de conformidade (verde = bom; vermelho = ruim).
const corClassificacao = (c) => {
  if (c === 'Excelente')    return { bg: '#d1fae5', color: '#065f46', icone: '🟢', rotulo: 'Excelente' };
  if (c === 'Satisfatorio') return { bg: '#ecfccb', color: '#3f6212', icone: '🟢', rotulo: 'Satisfatório' };
  if (c === 'Atencao')      return { bg: '#fef3c7', color: '#92400e', icone: '🟡', rotulo: 'Atenção' };
  if (c === 'Critico')      return { bg: '#fee2e2', color: '#991b1b', icone: '🔴', rotulo: 'Crítico' };
  if (c === 'NA')           return { bg: '#f1f5f9', color: '#475569', icone: '⚪', rotulo: 'N/A' };
  return { bg: '#f1f5f9', color: '#475569', icone: '', rotulo: c || '-' };
};

// Faixas de conformidade, para a legenda da consulta — os cortes espelham o
// `classificarConformidade` do backend (server/operacional/bsv/score-utils.js), que
// é a fonte da verdade. ⚠️ Mudou a faixa lá, muda o texto aqui.
// Por que a legenda existe nesta tela: até 2026-08-26 ela morava SÓ no pop-up
// "Nova Avaliação" — invisível para quem apenas consulta (o `Gerente`, que
// entrou nesse dia, nunca abre o pop-up) e, portanto, invisível justamente para
// quem recebe a nota da própria loja e precisa saber o que ela significa.
const FAIXAS_LEGENDA = [
  { classif: 'Excelente',    faixa: '90 a 100%' },
  { classif: 'Satisfatorio', faixa: '80 a 89%' },
  { classif: 'Atencao',      faixa: '60 a 79%' },
  { classif: 'Critico',      faixa: 'abaixo de 60%' },
];

const formatarDataBR = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('pt-BR');
};

// Célula base da tabela dinâmica (cabeçalho e corpo).
const thBase = { padding: '12px 20px', borderBottom: '1px solid #e2e8f0', fontSize: 12.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdBase = { padding: '12px 20px', fontSize: 14, color: '#0f172a', verticalAlign: 'middle' };

// `acoesExtras`: nó opcional com botões renderizado no cabeçalho (ex.: "Nova
// Avaliação" + "Gerenciar versões", quando esta tela é a inicial do sub-hub).
const BsvHistorico = ({ acoesExtras }) => {
  const compacto = useEhCompacto();
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [detalheId, setDetalheId] = useState(null);

  // Filtros (client-side — a lista é pequena e o filtro é instantâneo).
  const [fLoja, setFLoja] = useState('');
  const [fVersao, setFVersao] = useState('');
  const [fClassif, setFClassif] = useState('');
  const [fPeriodo, setFPeriodo] = useState(''); // mês de referência 'AAAA-MM' (input type=month)

  const carregar = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const r = await fetch(`${API_BASE}/historico`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setAvaliacoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar as avaliações.');
      console.error(e);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // Opções de loja/versão derivadas dos dados (a classificação é enum fixo).
  const lojas = useMemo(() => [...new Set(avaliacoes.map((a) => a.LOJA).filter(Boolean))].sort(), [avaliacoes]);
  const versoes = useMemo(() => [...new Set(avaliacoes.map((a) => a.NOME_VERSAO).filter(Boolean))].sort(), [avaliacoes]);

  // Data de referência da avaliação (a data da visita, não a de gravação).
  const dataRef = (a) => String(a.DATA_AVALIACAO || a.DATA_CRIACAO || '').slice(0, 10);

  const listaFiltrada = useMemo(() => avaliacoes.filter((a) => {
    if (fLoja && a.LOJA !== fLoja) return false;
    if (fVersao && a.NOME_VERSAO !== fVersao) return false;
    if (fClassif && a.CLASSIFICACAO !== fClassif) return false;
    const d = dataRef(a);
    // Período = mês de referência (AAAA-MM): compara só o prefixo ano-mês.
    if (fPeriodo && (!d || d.slice(0, 7) !== fPeriodo)) return false;
    return true;
  }), [avaliacoes, fLoja, fVersao, fClassif, fPeriodo]);

  const temFiltro = fLoja || fVersao || fClassif || fPeriodo;
  const limparFiltros = () => { setFLoja(''); setFVersao(''); setFClassif(''); setFPeriodo(''); };

  // Árvore Loja → Versão → Avaliações (tabela dinâmica em memória, a partir da
  // lista já filtrada). Lojas e versões em ordem alfabética; dentro de cada
  // versão, a avaliação mais recente primeiro.
  const arvore = useMemo(() => {
    const porLoja = new Map();
    for (const a of listaFiltrada) {
      const loja = a.LOJA || '—';
      if (!porLoja.has(loja)) porLoja.set(loja, new Map());
      const vs = porLoja.get(loja);
      const v = a.NOME_VERSAO || '—';
      if (!vs.has(v)) vs.set(v, []);
      vs.get(v).push(a);
    }
    return [...porLoja.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([loja, vs]) => {
      const versoesDaLoja = [...vs.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([versao, avals]) => ({
        versao,
        avaliacoes: avals.slice().sort((x, y) => dataRef(y).localeCompare(dataRef(x))),
      }));
      const todas = versoesDaLoja.flatMap((v) => v.avaliacoes);
      // Contagem por classificação (só as presentes), preservando a ordem canônica.
      const classifContagem = CLASSIF_OPCOES
        .map((c) => ({ classificacao: c, n: todas.filter((a) => a.CLASSIFICACAO === c).length }))
        .filter((x) => x.n > 0);
      return { loja, versoes: versoesDaLoja, totalAvaliacoes: todas.length, totalVersoes: versoesDaLoja.length, classifContagem };
    });
  }, [listaFiltrada]);

  // Estado de expansão da árvore (persistente entre filtros — nós invisíveis só
  // não renderizam). Loja por nome; versão pela chave `loja|versão`.
  const [lojasAbertas, setLojasAbertas] = useState([]);
  const [versoesAbertas, setVersoesAbertas] = useState([]);
  const alternarLoja = (loja) => setLojasAbertas((p) => p.includes(loja) ? p.filter((l) => l !== loja) : [...p, loja]);
  const alternarVersao = (chave) => setVersoesAbertas((p) => p.includes(chave) ? p.filter((c) => c !== chave) : [...p, chave]);

  // Exporta a lista JÁ FILTRADA. É gerado no cliente (e não pelo backend, como
  // antes) justamente porque os filtros agora vivem só aqui — o servidor não
  // saberia o que o usuário está vendo na tela.
  const exportarExcel = () => {
    const linhas = listaFiltrada.map((a) => ({
      'Avaliação': a.ID, 'Versão': a.NOME_VERSAO, 'Loja': a.LOJA,
      'Data': formatarDataBR(a.DATA_AVALIACAO),
      'Avaliador': a.AVALIADOR_RESPONSAVEL || '', 'Gerente de Negócios': a.GERENTE_NEGOCIOS || '',
      'Gerente de Plantão': a.GERENTE_PLANTAO || '',
      'Conformidades': a.SCORE_TOTAL, 'Conformidade (%)': a.PERCENTUAL_CONFORMIDADE,
      'Classificação': corClassificacao(a.CLASSIFICACAO).rotulo,
      'Observações': a.OBSERVACAO_GERAL || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhas), 'Avaliações BSV');
    XLSX.writeFile(wb, `AvaliacoesBSV_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (detalheId) return <DetalheAvaliacao id={detalheId} onVoltar={() => setDetalheId(null)} />;

  // No compacto (celular) cada filtro ocupa a linha inteira — senão os selects
  // ficam espremidos e cortados. No desktop, largura automática lado a lado.
  const selStyle = {
    padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, color: '#475569', background: '#fff',
    ...(compacto ? { width: '100%', boxSizing: 'border-box' } : {}),
  };
  const filtroItem = compacto ? { flex: '1 1 100%' } : {};

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, flex: 1, minWidth: 180, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Avaliações gravadas</h3>
        {acoesExtras}
        {listaFiltrada.length > 0 && (
          <button onClick={exportarExcel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}>
            <FileDown size={16} /> Exportar Excel
          </button>
        )}
      </div>

      {/* Barra de filtros */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 16 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569', fontWeight: 700, fontSize: 13 }}><Filter size={15} /> Filtros</span>
        <select value={fLoja} onChange={(e) => setFLoja(e.target.value)} style={selStyle}>
          <option value="">Todas as lojas</option>
          {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={fVersao} onChange={(e) => setFVersao(e.target.value)} style={selStyle}>
          <option value="">Todos os formulários</option>
          {versoes.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={fClassif} onChange={(e) => setFClassif(e.target.value)} style={selStyle}>
          <option value="">Todas as classificações</option>
          {CLASSIF_OPCOES.map((c) => <option key={c} value={c}>{corClassificacao(c).rotulo}</option>)}
        </select>
        <label style={{ fontSize: 13, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 6, ...filtroItem }}>Período <input type="month" value={fPeriodo} onChange={(e) => setFPeriodo(e.target.value)} style={selStyle} /></label>
        {temFiltro && (
          <button onClick={limparFiltros} style={{ padding: '8px 12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Limpar</button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#94a3b8' }}>{listaFiltrada.length} de {avaliacoes.length}</span>
      </div>

      {/* Legenda das faixas — o que cada cor da coluna "Classificação" quer dizer. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
        marginTop: -8, marginBottom: 16, paddingLeft: 2,
      }}>
        <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700 }}>Faixas de conformidade</span>
        {FAIXAS_LEGENDA.map(({ classif, faixa }) => {
          const c = corClassificacao(classif);
          return (
            <span key={classif} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: c.bg, color: c.color, borderRadius: 999,
              padding: '3px 10px', fontSize: 12, fontWeight: 700,
            }}>
              {c.rotulo}
              {/* tabular-nums: os números das faixas ficam alinhados entre as pastilhas */}
              <span style={{ fontWeight: 600, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>{faixa}</span>
            </span>
          );
        })}
      </div>

      {erro && <div style={alerta}>{erro}</div>}
      {carregando ? <p style={{ color: '#64748b' }}>Carregando…</p> : avaliacoes.length === 0 ? (
        <div style={vazio}>Nenhuma avaliação gravada ainda.</div>
      ) : listaFiltrada.length === 0 ? (
        <div style={vazio}>Nenhuma avaliação para os filtros escolhidos.</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: compacto ? 560 : 760 }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ ...thBase, textAlign: 'left' }}>Loja / Formulário</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Classificação</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Conformidade</th>
                  <th style={{ ...thBase, textAlign: 'left' }}>Data</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {arvore.map((L) => {
                  const lojaAberta = lojasAbertas.includes(L.loja);
                  return (
                    <React.Fragment key={L.loja}>
                      {/* Nível 1 — a loja (resumo: nº avaliações · nº formulários · contagem por classificação) */}
                      <tr onClick={() => alternarLoja(L.loja)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: lojaAberta ? '#f8fafc' : '#fff' }}>
                        <td style={{ ...tdBase, fontWeight: 800, color: '#0f172a' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            {lojaAberta ? <ChevronDown size={16} color="#475569" /> : <ChevronRight size={16} color="#94a3b8" />}
                            <Store size={16} color="#94a3b8" /> {L.loja}
                            <span style={{ fontWeight: 500, color: '#94a3b8', fontSize: 12.5 }}>
                              · {L.totalAvaliacoes} avaliaç{L.totalAvaliacoes > 1 ? 'ões' : 'ão'} · {L.totalVersoes} formulário{L.totalVersoes > 1 ? 's' : ''}
                            </span>
                          </span>
                        </td>
                        <td style={tdBase} colSpan={4}>
                          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
                            {L.classifContagem.map(({ classificacao, n }) => {
                              const c = corClassificacao(classificacao);
                              return (
                                <span key={classificacao} style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: c.bg, color: c.color }}>
                                  {n} {c.rotulo}
                                </span>
                              );
                            })}
                          </span>
                        </td>
                      </tr>

                      {/* Nível 2 — o formulário (versão) */}
                      {lojaAberta && L.versoes.map((V) => {
                        const chaveV = `${L.loja}|${V.versao}`;
                        const versaoAberta = versoesAbertas.includes(chaveV);
                        return (
                          <React.Fragment key={chaveV}>
                            <tr onClick={() => alternarVersao(chaveV)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: '#f8fafc' }}>
                              <td style={{ ...tdBase, padding: compacto ? '10px 10px 10px 22px' : '10px 20px 10px 44px', fontWeight: 700, color: '#334155' }} colSpan={5}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                  {versaoAberta ? <ChevronDown size={14} color="#64748b" /> : <ChevronRight size={14} color="#cbd5e1" />}
                                  <ClipboardList size={14} color="#94a3b8" /> {V.versao}
                                  <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: 12.5 }}>({V.avaliacoes.length})</span>
                                </span>
                              </td>
                            </tr>

                            {/* Nível 3 — as avaliações do formulário */}
                            {versaoAberta && V.avaliacoes.map((a) => {
                              const c = corClassificacao(a.CLASSIFICACAO);
                              return (
                                <tr key={a.ID} onClick={() => setDetalheId(a.ID)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: '#fff' }}>
                                  <td style={{ ...tdBase, padding: compacto ? '10px 10px 10px 36px' : '10px 20px 10px 72px' }}>
                                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 13.5 }}>
                                      #{a.ID}{a.AVALIADOR_RESPONSAVEL ? ` · ${a.AVALIADOR_RESPONSAVEL}` : ''}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, fontStyle: 'italic' }}>
                                      {a.GERENTE_NEGOCIOS ? `GN: ${a.GERENTE_NEGOCIOS}` : ''}
                                      {a.GERENTE_NEGOCIOS && a.GERENTE_PLANTAO ? ' · ' : ''}
                                      {a.GERENTE_PLANTAO ? `GP: ${a.GERENTE_PLANTAO}` : ''}
                                    </div>
                                  </td>
                                  <td style={tdBase}>
                                    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800, background: c.bg, color: c.color, whiteSpace: 'nowrap' }}>
                                      {c.icone} {c.rotulo}
                                    </span>
                                  </td>
                                  <td style={{ ...tdBase, textAlign: 'right' }}>
                                    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                      <span style={{ fontSize: 15, fontWeight: 800, color: c.color, fontVariantNumeric: 'tabular-nums' }}>{a.PERCENTUAL_CONFORMIDADE}%</span>
                                      <span style={{ fontSize: 11, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{a.SCORE_TOTAL} conformidades</span>
                                    </span>
                                  </td>
                                  <td style={{ ...tdBase, fontSize: 13, color: '#475569', fontVariantNumeric: 'tabular-nums' }}>
                                    {formatarDataBR(a.DATA_AVALIACAO) || <span style={{ color: '#cbd5e1' }}>—</span>}
                                  </td>
                                  <td style={{ ...tdBase, textAlign: 'right' }}>
                                    <ChevronRight size={18} color="#cbd5e1" />
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Detalhe (mesmo arquivo p/ reduzir fragmentação)
// ---------------------------------------------------------------------
const DetalheAvaliacao = ({ id, onVoltar }) => {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/avaliacao/${id}`, { headers: auth() });
        const data = await r.json();
        if (!r.ok) throw new Error(data?.message || 'Erro');
        setDados(data);
      } catch (e) { setErro(e.message); }
    })();
  }, [id]);

  const exportarDetalhe = async () => {
    try {
      const r = await fetch(`${API_BASE}/avaliacao/${id}?formato=xlsx`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `AvaliacaoBSV_${id}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErro('Erro ao exportar: ' + e.message); }
  };

  const exportarDetalhePdf = async () => {
    try {
      const r = await fetch(`${API_BASE}/avaliacao/${id}?formato=pdf`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `AvaliacaoBSV_${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErro('Erro ao exportar PDF: ' + e.message); }
  };

  if (erro) return (
    <div>
      <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
      <div style={{ ...alerta, marginTop: 12 }}>{erro}</div>
    </div>
  );
  if (!dados) return <p style={{ color: '#64748b' }}>Carregando…</p>;

  const { avaliacao, respostas, resumoPorArea = [] } = dados;
  const c = corClassificacao(avaliacao.CLASSIFICACAO);

  const porArea = respostas.reduce((acc, r) => {
    if (!acc[r.AREA]) acc[r.AREA] = [];
    acc[r.AREA].push(r);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
        <h3 style={{ margin: 0, flex: 1, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          Avaliação BSV #{avaliacao.ID} — {avaliacao.LOJA} · {formatarDataBR(avaliacao.DATA_AVALIACAO)}
        </h3>
        <button onClick={exportarDetalhePdf} style={btnVermelho}>
          <FileText size={16} /> Exportar PDF
        </button>
        <button onClick={exportarDetalhe} style={btnVerde}>
          <Download size={16} /> Exportar XLSX
        </button>
      </div>

      <div style={{
        background: c.bg, border: `2px solid ${c.color}`,
        borderRadius: 12, padding: 16, marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 28 }}>{c.icone}</div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>CONFORMIDADE GERAL</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>
            {avaliacao.SCORE_TOTAL} conformidades · {avaliacao.PERCENTUAL_CONFORMIDADE}% · {c.rotulo}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#475569' }}>
          <div>Formulário: <strong>{avaliacao.NOME_VERSAO}</strong></div>
          <div>Avaliador: <strong>{avaliacao.AVALIADOR_RESPONSAVEL || '-'}</strong></div>
          <div>G. Negócios: <strong>{avaliacao.GERENTE_NEGOCIOS || '-'}</strong></div>
          <div>G. Plantão: <strong>{avaliacao.GERENTE_PLANTAO || '-'}</strong></div>
        </div>
      </div>

      {/* Conformidade por área */}
      {resumoPorArea.length > 0 && (
        <div style={card}>
          <h4 style={{ margin: '0 0 12px', color: '#0f172a' }}>Conformidade por Área</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#334155' }}>
              <tr>
                {['Área', 'Conformidades', '%', 'Classificação'].map((t, i) => (
                  <th key={t} style={{ ...th, color: '#fff', borderBottom: 'none', textAlign: i === 0 ? 'left' : 'center' }}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resumoPorArea.map((a) => {
                const ca = corClassificacao(a.classificacao);
                return (
                  <tr key={a.idArea} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={td}>{a.area}</td>
                    <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{a.classificacao === 'NA' ? '—' : `${a.score}/${a.pesoMaximo}`}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{a.classificacao === 'NA' ? '—' : `${Math.round(a.percentualConformidade)}%`}</td>
                    <td style={{ ...td, textAlign: 'center', background: ca.bg, color: ca.color, fontWeight: 700 }}>
                      {ca.icone} {ca.rotulo}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid #334155' }}>
                <td style={{ ...td, fontWeight: 800 }}>RESULTADO GERAL</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 800 }}>{avaliacao.SCORE_TOTAL}</td>
                <td style={{ ...td, textAlign: 'center', fontWeight: 800 }}>{Math.round(avaliacao.PERCENTUAL_CONFORMIDADE)}%</td>
                <td style={{ ...td, textAlign: 'center', background: c.bg, color: c.color, fontWeight: 800 }}>
                  {c.icone} {c.rotulo}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Respostas por área */}
      {Object.entries(porArea).map(([area, perguntas]) => (
        <div key={area} style={card}>
          <h4 style={{ margin: '0 0 12px', color: '#0f172a' }}>{area}</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th style={th}>Pergunta</th>
                <th style={{ ...th, width: 90, textAlign: 'center' }}>Atende</th>
              </tr>
            </thead>
            <tbody>
              {perguntas.map((p) => (
                <tr key={p.ID_PERGUNTA} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={td}>{p.TEXTO}</td>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: p.NAO_APLICAVEL ? '#64748b' : (p.ATENDE === 1 ? '#059669' : '#dc2626') }}>
                    {p.NAO_APLICAVEL ? 'N/A' : (p.ATENDE === 1 ? 'Sim' : 'Não')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {avaliacao.OBSERVACAO_GERAL && (
        <div style={card}>
          <h4 style={{ margin: '0 0 8px', color: '#0f172a' }}>Observações gerais</h4>
          <p style={{ margin: 0, color: '#475569', whiteSpace: 'pre-wrap' }}>{avaliacao.OBSERVACAO_GERAL}</p>
        </div>
      )}
    </div>
  );
};

// estilos
const btnVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 };
const alerta = { padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 };
const vazio = { padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' };
const th = { padding: 10, textAlign: 'left', fontWeight: 800, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const td = { padding: 10, color: '#0f172a' };
const btnVerde = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
  background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};
const btnVermelho = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
  background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};

export default BsvHistorico;
